import * as React from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import {
    actionCreators as uiActionCreators,
    getActiveNavigationComponent,
    getFastTrackStatus,
} from "ui/store";
import {
    getSelectedDayModuleElements,
    getSelectedWeekModuleElements,
    getMetadata,
    actionCreators as burdeContentActionCreators,
    getBorderImage,
    getSelectedLayout,
    getSelectedFont,
    getFontOptions,
} from "burde-api-content/store";
import * as cn from "classnames/bind";
import * as styles from "./insert.css";
import { drawCalendar } from "svg/draw/draw-calendar";
import {
    BurdeFontResource,
    BurdeFontResponse,
    CalendarDrawerContent,
    CalendarDrawerSettings,
    CalendarState,
    DateMetaData,
    ImageDetails,
    RubberBandItem,
    SpiralItem,
} from "burde";
import { ApplicationState } from "store";
import {
    DayModuleElement,
    NavigationComponent,
    SvgCalendarLayout,
    WeekModuleElement,
} from "ui/constants";
import { getResource } from "resource/store";
import { isEqual } from "lodash";
import { fetchDayModuleElements, fetchWeekModuleElements } from "ui/helper";
import { getLayoutById } from "ui/service";

const cx = cn.bind(styles);

type CalendarInsertProps = {
    calendar: CalendarState;
    spiral: SpiralItem | null;
    rubberband: RubberBandItem | null;
    stepIsDimmed?: boolean;
    dimmedText?: string;
    activeComponent: NavigationComponent;
    weekNumberPrefix: string;
    fastTrackStatus: boolean;
    selectedLayout: SvgCalendarLayout | null;
    selectedDayModuleElements: DayModuleElement[];
    selectedWeekModuleElements: WeekModuleElement[];
    metadata: DateMetaData[] | null;
    borderImage: ImageDetails | null;
    fontOptions: BurdeFontResponse | null;
    selectedFont: BurdeFontResource | null;
} & typeof uiActionCreators &
    typeof burdeContentActionCreators;

class CalendarInsert extends React.Component<
    CalendarInsertProps & RouteComponentProps
> {
    private _divRef?: HTMLDivElement | null;
    private drawCalendarInsert = (props: CalendarInsertProps) => {
        const {
            calendar,
            activeComponent,
            weekNumberPrefix,
            selectedLayout,
            selectedDayModuleElements,
            selectedWeekModuleElements,
            metadata,
            spiral,
            rubberband,
            borderImage,
            fontOptions,
            selectedFont,
        } = props;

        if (
            !this._divRef ||
            !metadata ||
            metadata.length === 0 ||
            !selectedLayout
        ) {
            return;
        }

        const calendarDrawerSettings: CalendarDrawerSettings = {
            isConfiguratorView: true,
            drawCoverOnly: false,
            drawInsertOnly: false,
            numberOfWeeks: 1,
            drawWeekModulePlaceholders: activeComponent.name === "weekModules",
        };

        const calendarDrawerContent: CalendarDrawerContent = {
            article: null,
            calendarId: null,
            layout: selectedLayout,
            dayModules: selectedDayModuleElements,
            weekModules: selectedWeekModuleElements,
            metadata: metadata ? metadata : [],
            coverImage: null,
            backCoverImage: null,
            borderImage: borderImage,
            customCoverImage: null,
            customBackImage: null,
            extraPages: null,
            salesPages: null,
            customerInformation: null,
            ownerPage: null,
            monthDivider: null,
            ownerPageImageUrl: "",
            weekNumberPrefix: weekNumberPrefix,
            spiral: spiral,
            rubberband: rubberband,
            calendarName: null,
            fonts: fontOptions,
        };

        drawCalendar(
            calendar,
            calendarDrawerSettings,
            calendarDrawerContent,
            this._divRef,
        );
    };

    public async componentDidMount() {
        const { fastTrackStatus, SetFastTrack } = this.props;

        if (fastTrackStatus) {
            SetFastTrack(false);
        }

        this.drawCalendarInsert(this.props);
    }

    public async componentDidUpdate(prevProps: CalendarInsertProps) {
        this.drawCalendarInsert(this.props);
    }

    public render() {
        const { stepIsDimmed, dimmedText } = this.props;

        const svgContainerCn = () =>
            cx({
                svgContainer: true,
                "svg-container": true,
            });

        const calendarContainerCn = () =>
            cx({
                calendarContainer: true,
            });

        const dimmedTextCn = () =>
            cx({
                dimmedText: true,
            });

        const dimmerCn = () =>
            cx({
                dimmerContainer: true,
                dimmer: stepIsDimmed !== undefined && stepIsDimmed === true,
            });

        return (
            <div key={"calendar-content"} className={calendarContainerCn()}>
                {dimmedText !== undefined && dimmedText !== "" && (
                    <div className={dimmedTextCn()}>
                        <strong>{dimmedText}</strong>
                    </div>
                )}
                <div className={dimmerCn()}>
                    <div
                        className={svgContainerCn()}
                        ref={(divRef) => (this._divRef = divRef)}
                    />
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    activeComponent: getActiveNavigationComponent(state),
    weekNumberPrefix: getResource(state, {
        key: "Header_WeekNumberPrefix",
    }),
    fastTrackStatus: getFastTrackStatus(state),
    selectedLayout: getSelectedLayout(state),
    selectedDayModuleElements: getSelectedDayModuleElements(state),
    selectedWeekModuleElements: getSelectedWeekModuleElements(state),
    metadata: getMetadata(state),
    borderImage: getBorderImage(state),
    fontOptions: getFontOptions(state),
    selectedFont: getSelectedFont(state),
});

const mapDispatchToProps = {
    ...uiActionCreators,
    ...burdeContentActionCreators,
};

const ConnectedCalendarInsert = connect(
    mapStateToProps,
    mapDispatchToProps,
)(CalendarInsert);

export default withRouter(ConnectedCalendarInsert);
